import * as React from "react";
import { connect } from "react-redux";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SubPageLayout } from "../../../components/layouts/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { FC, useEffect, useMemo } from "react";
import { ChangeOptions, Form, ModelAction, actions } from "react-redux-form";
import * as TrialInstitutionItemActions from "../../../actions/trialInstitutionItem";
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { ITrialInstitutionItemReduxFormState } from "../../../reducers/reactReduxForms/trialInstitutionItem";

import "./TrialInstitutionItemCreateVersion.scss";

const reduxFormName: string = "reduxForms.trialInstitutionItem";

interface TrialInstitutionItemCreateVersionProps {
    currentTrialInstitutionItem: Dtos.TrialInstitutionItem;
    trialInstitutionId?: number;
    form?: Dtos.TrialInstitutionItem & IRegistrationFormState;
    formState?: Dtos.FormState;
    formProperties?: Dtos.FormProperty[];
    reduxFormState?: ITrialInstitutionItemReduxFormState;
    trialInstitutionItem?: Dtos.TrialInstitutionItem;
    lookups?: Dtos.Lookup[];
    loadingTrialInstitutionItem?: boolean;
    savingTrialInstitutionItem?: boolean;
    creatingTrialInstitutionItem?: boolean;
    createTrialInstitutionItemSuccess?: boolean;
    validationFailures?: Dtos.ResponseError[];
    onClose?: Function;
    clearTrialInstitutionItem?: TrialInstitutionItemActions.ITrialInstitutionItemClearActionCreator;
    saveTrialInstitutionItem?: TrialInstitutionItemActions.ITrialInstitutionItemSaveActionCreator;
    createTrialInstitutionItem?: TrialInstitutionItemActions.ITrialInstitutionItemCreateActionCreator;
    loadForm?: (model: string, value: any) => ModelAction;
    changeForm?: (
        model: string,
        value: any,
        options?: ChangeOptions
    ) => ModelAction;
}

const TrialInstitutionItemCreateVersion: FC<
    TrialInstitutionItemCreateVersionProps
> = ({
    currentTrialInstitutionItem,
    trialInstitutionId,
    form,
    formState,
    formProperties,
    trialInstitutionItem,
    lookups,
    loadingTrialInstitutionItem,
    savingTrialInstitutionItem,
    creatingTrialInstitutionItem,
    createTrialInstitutionItemSuccess,
    validationFailures,
    onClose,
    clearTrialInstitutionItem,
    saveTrialInstitutionItem,
    createTrialInstitutionItem,
    loadForm,
    changeForm,
}) => {
    // Create Trial Institution Item
    useEffect(() => {
        if (!trialInstitutionItem && trialInstitutionId) {
            createTrialInstitutionItem(trialInstitutionId);
        }
    }, [trialInstitutionItem, trialInstitutionId, createTrialInstitutionItem]);

    // Load Form
    useEffect(() => {
        if (
            !creatingTrialInstitutionItem &&
            createTrialInstitutionItemSuccess
        ) {
            loadReduxForm();
        }
    }, [
        creatingTrialInstitutionItem,
        createTrialInstitutionItemSuccess,
        loadForm,
    ]);

    // Cleanup effect to clear the trial institution item on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionItem();
        };
    }, [clearTrialInstitutionItem]);

    const handleOnSubmit = (
        data: Dtos.TrialInstitutionItem,
        currentTrialInstitutionItem: Dtos.TrialInstitutionItem
    ) => {
        if (!data.version) {
            handleOnSubmitFailed(data);
            return;
        }

        if (data) {
            const changes: Dtos.TrialInstitutionItem = {
                trialInstitutionId:
                    currentTrialInstitutionItem.trialInstitutionId,
                trialInstitutionItemTemplateId:
                    currentTrialInstitutionItem.trialInstitutionItemTemplateId,
                active: true,
            } as Dtos.TrialInstitutionItem;
            saveTrialInstitutionItem({ ...data, ...changes }, null);
        }
        onCancel();
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialInstitutionItem) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialInstitutionItem =
            {} as Dtos.TrialInstitutionItem;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialInstitutionItem }, extraData)
        );
    };

    const renderContent = useMemo(() => {
        const formDisabled: boolean =
            savingTrialInstitutionItem ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            version: {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled,
                validators: {
                    required: (value) =>
                        value != undefined &&
                        value != null &&
                        value?.length > 0,
                },
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) =>
                        handleOnSubmit(val, currentTrialInstitutionItem)
                    }
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialInstitutionItem",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        currentTrialInstitutionItem,
    ]);

    return (
        <SubPageLayout loading={loadingTrialInstitutionItem}>
            {renderContent}
        </SubPageLayout>
    );
};

const mapStateToProps = (
    state: ITmdState,
    ownProps: TrialInstitutionItemCreateVersionProps
): Partial<TrialInstitutionItemCreateVersionProps> => {
    let trialInstitutionItem: Dtos.TrialInstitutionItem | undefined = !(
        state.trialInstitutionItem.formData instanceof Array
    )
        ? state.trialInstitutionItem.formData
        : undefined;

    return {
        currentTrialInstitutionItem: ownProps.currentTrialInstitutionItem,
        trialInstitutionId: ownProps.trialInstitutionId,
        trialInstitutionItem: trialInstitutionItem!,
        loadingTrialInstitutionItem:
            state.trialInstitutionItem.loadState &&
            state.trialInstitutionItem.loadState.status == RequestState.Pending,
        savingTrialInstitutionItem:
            state.trialInstitutionItem.saveState &&
            state.trialInstitutionItem.saveState.status == RequestState.Pending,
        creatingTrialInstitutionItem:
            state.trialInstitutionItem.createState &&
            state.trialInstitutionItem.createState.status ==
                RequestState.Pending,
        createTrialInstitutionItemSuccess:
            state.trialInstitutionItem.createState &&
            state.trialInstitutionItem.createState.status ==
                RequestState.Success,
        lookups: state.trialInstitutionItem.lookups,
        form: state.reduxForms.trialInstitutionItem,
        formState: state.trialInstitutionItem.formState,
        formProperties: state.trialInstitutionItem.formProperties,
        reduxFormState: state.reduxForms.formState.trialInstitutionItem,
        validationFailures: state.trialInstitutionItem.validationFailures,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionItemCreateVersionProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        clearTrialInstitutionItem: bindActionCreators(
            TrialInstitutionItemActions.Clear,
            dispatch
        ),
        saveTrialInstitutionItem: bindActionCreators(
            TrialInstitutionItemActions.SaveTrialInstitutionItem,
            dispatch
        ),
        createTrialInstitutionItem: bindActionCreators(
            TrialInstitutionItemActions.CreateTrialInstitutionItem,
            dispatch
        ),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionItemCreateVersion);
